import { useMemo, useRef, useState } from "react";
import { FaCheck, FaExclamation, FaRightFromBracket, FaUser } from "react-icons/fa6";
import "./profile-modal.style.scss";

import { useFormik } from "formik";
import * as yup from "yup";

import { isEmptyObject } from "../../global/function";
import { useQueryClient } from "@tanstack/react-query";

import TextField from "../form-inputs/text-field.component";
import FormError from "../form-error/form-error.component";
import InlineNotice from "../inline-notice/inline-notice.component";

import { sessionQueryKey, useLogout, useSession, useUpdatePassword } from "../../queries/auth.queries";
import { useUploadImage } from "../../queries/image.queries";

const changePswrdSchema = yup.object().shape({
	oldPassword: yup
		.string()
		.required("Old Password is required")
		.min(8, "Password must be at least 8 characters"),
	password: yup
		.string()
		.required("New Password is required")
		.min(8, "Password must be at least 8 characters"),
	confirm_password: yup
		.string()
		.oneOf([yup.ref("password"), null], "Passwords must match")
		.required("Confirm password cannot be empty"),
});

function ProfileModal({ isVisible }: { isVisible: boolean }) {
	const { data: session } = useSession();
	const logoutMutation = useLogout();
	const updatePasswordMutation = useUpdatePassword();
	const uploadImageMutation = useUploadImage();
	const queryClient = useQueryClient();

	const [selectedImage, setSelectedImage] = useState<File | null>(null);
	const [isChangePswrd, setIsChangePswrd] = useState(false);
	const [imageSizeNotice, setImageSizeNotice] = useState<string | null>(null);

	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleImageEdit = () => {
		fileInputRef.current?.click();
	};

	const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = e.target.files?.[0];
		if (!selectedFile) return;

		if (selectedFile.size > 2048576) {
			setImageSizeNotice("File size exceeds the limit");
			return;
		}

		setSelectedImage(selectedFile);

		try {
			const uploadResult = await uploadImageMutation.mutateAsync({
				image: selectedFile,
				id: session?.user_info_id ?? "",
				type: "user",
			});

			const newImageUrl = uploadResult.result?.user_image;
			if (newImageUrl) {
				queryClient.setQueryData(sessionQueryKey, (old: typeof session) =>
					old ? { ...old, user_image: newImageUrl } : old,
				);
			}
		} catch {
			// FormError (bound to uploadImageMutation) renders the message
		}
	};

	const handleLogout = () => {
		if (logoutMutation.isPending) return;
		logoutMutation.mutate();
	};

	const imgSrc = useMemo(() => {
		if (selectedImage) return URL.createObjectURL(selectedImage);
		if (!isEmptyObject(session) && session?.user_image) return session.user_image;
		return "";
	}, [session, selectedImage]);

	const formik = useFormik({
		validationSchema: changePswrdSchema,
		initialValues: {
			oldPassword: "",
			password: "",
			confirm_password: "",
		},
		onSubmit: (pswrdData, { setSubmitting }) => {
			updatePasswordMutation.mutate(
				{ ...pswrdData, user_info_id: session?.user_info_id },
				{
					// Only leave the change-password view on success — staying on
					// it on error is what lets FormError (bound to this mutation)
					// actually be seen. A successful change forces a full logout
					// anyway (useUpdatePassword clears the session), so this view
					// closing is moot there — ProtectedRoute redirects to /login.
					onSuccess: () => setIsChangePswrd(false),
					onSettled: () => setSubmitting(false),
				},
			);
		},
	});

	const isPending =
		logoutMutation.isPending ||
		updatePasswordMutation.isPending ||
		uploadImageMutation.isPending;

	return (
		<>
			{!isEmptyObject(session) && (
				<div
					className="modal-profile-container"
					style={isVisible ? { display: "block" } : { display: "none" }}
					onClick={(e) => e.stopPropagation()}
				>
					<div className="container-pointer"></div>
					{isChangePswrd ? (
						<div className="modal-profile-container-inner">
							<h2 className="profile-maintext">Change Password</h2>
							<p className="profile-subtext">
								Please enter and confirm your new password to
								access your account.
							</p>

							<FormError
								isError={updatePasswordMutation.isError}
								error={updatePasswordMutation.error}
								fallback="Failed to change password."
							/>

							<div className="change-pswrd-wrapper">
								<TextField
									name="oldPassword"
									label="Old Password"
									placeholder="Enter old password"
									formik={formik}
									disabled={!isChangePswrd}
									securetext
								/>
								<TextField
									name="password"
									label="New Password"
									placeholder="Enter new password"
									formik={formik}
									disabled={!isChangePswrd}
									securetext
								/>
								<TextField
									name="confirm_password"
									label="Confirm Password"
									placeholder="Re-enter new password"
									formik={formik}
									disabled={!isChangePswrd}
									securetext
								/>
								<div className="btns-container">
									<div
										className="change-pswrd-btns"
										onClick={() => setIsChangePswrd(false)}
									>
										Back
									</div>
									<div
										className="change-pswrd-btns"
										onClick={() =>
											!formik.isSubmitting && formik.submitForm()
										}
									>
										Reset Password
									</div>
								</div>
							</div>
						</div>
					) : (
						<div className="modal-profile-container-inner">
							<div
								className="profile-img-container"
								onClick={handleImageEdit}
							>
								<div className="profile-img">
									{imgSrc ? (
										<img
											crossOrigin="anonymous"
											src={imgSrc}
											alt="profile"
										/>
									) : (
										<FaUser size="5em" />
									)}
								</div>
								<div
									className={`profile-verified ${
										session?.user_verified ? "active" : "inactive"
									}`}
								>
									{session?.user_verified ? (
										<FaCheck />
									) : (
										<FaExclamation />
									)}
								</div>
							</div>
							<input
								type="file"
								accept="image/*"
								ref={fileInputRef}
								style={{ display: "none" }}
								onChange={handleImageChange}
							/>
							<InlineNotice
								message={imageSizeNotice}
								type="warning"
								onDismiss={() => setImageSizeNotice(null)}
							/>
							<FormError
								isError={uploadImageMutation.isError}
								error={uploadImageMutation.error}
								fallback="Failed to upload image."
							/>
							<div className="profile-details">
								<h2 className="profile-maintext">
									{session?.first_name} {session?.last_name}
								</h2>
								<p className="profile-subtext">{session?.user_email}</p>
								<div className="profile-status-role">
									<div className="status-role-container">
										<p className="sr-label">Role</p>
										<h4 className="sr-value">{session?.user_role}</h4>
									</div>
									<div className="separator"></div>
									<div className="status-role-container">
										<p className="sr-label">Status</p>
										<h4 className="sr-value">{session?.user_status}</h4>
									</div>
								</div>
							</div>
							<div
								className="profile-action-btn"
								onClick={() => setIsChangePswrd(true)}
							>
								Change Password
							</div>
							<div
								className="profile-action-btn"
								onClick={handleLogout}
								style={isPending ? { opacity: 0.6 } : undefined}
							>
								Logout
								<FaRightFromBracket />
							</div>
						</div>
					)}
				</div>
			)}
		</>
	);
}

export default ProfileModal;
